import { type ReactNode, useEffect, useRef } from 'react';
import { clsx } from 'clsx';
import { X } from 'lucide-react';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg';
}

const sizeClass = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
};

/** Focusable controls inside the dialog body — exclude the header close button. */
const BODY_FOCUSABLE =
  'input, select, textarea, [href], button:not([aria-label="Close dialog"]), [tabindex]:not([tabindex="-1"])';

const ALL_FOCUSABLE =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

export function Modal({ open, onClose, title, children, size = 'md' }: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;

    previousFocus.current = document.activeElement as HTMLElement;
    const dialog = dialogRef.current;
    if (dialog) {
      const preferred = dialog.querySelector<HTMLElement>(BODY_FOCUSABLE);
      const fallback = dialog.querySelector<HTMLElement>(ALL_FOCUSABLE);
      (preferred ?? fallback)?.focus();
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab' || !dialog) return;

      const focusables = dialog.querySelectorAll<HTMLElement>(ALL_FOCUSABLE);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
      previousFocus.current?.focus();
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div
        className="absolute inset-0 bg-[var(--color-overlay)]"
        onClick={() => onCloseRef.current()}
        aria-hidden="true"
      />
      <div
        ref={dialogRef}
        className={clsx(
          'relative flex w-full flex-col overflow-hidden rounded-t-xl sm:rounded-xl border border-[var(--color-rule)] bg-[var(--color-surface)] shadow-[var(--shadow-brutal-lg)]',
          'max-h-[min(92dvh,90vh)]',
          'pb-[env(safe-area-inset-bottom)]',
          sizeClass[size],
        )}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-[var(--color-rule)] bg-[var(--color-surface)] px-4 py-3 sm:px-6 sm:py-4">
          <h2 id="modal-title" className="pr-2 text-lg font-semibold tracking-tight text-[var(--color-ink)]">
            {title}
          </h2>
          <button
            type="button"
            onClick={() => onCloseRef.current()}
            className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-md border border-[var(--color-rule)] bg-[var(--color-surface)] text-[var(--color-muted)] shadow-[var(--shadow-brutal-sm)] hover:bg-[var(--color-paper-2)] active:shadow-none"
            aria-label="Close dialog"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="overflow-y-auto overscroll-contain px-4 py-4 sm:px-6">{children}</div>
      </div>
    </div>
  );
}
